import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  Heading,
  Spinner,
  Text,
  Badge,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiMail, FiRefreshCw } from 'react-icons/fi';
import axiosInstance from '../../../api/axiosConfig';

export default function NewsletterTab() {
  const toast = useToast();
  
  const headingColor = useColorModeValue('brand.800', 'text.default');
  const subTextColor = useColorModeValue('brand.500', 'text.muted');
  const theadBg = useColorModeValue('brand.100', 'whiteAlpha.200');
  const waTheadBg = useColorModeValue('green.50', 'whiteAlpha.200');
  const rowHoverBg = useColorModeValue('brand.50', 'whiteAlpha.100');

  const bw = {
    box: {
      border: '1px solid',
      borderColor: useColorModeValue('brand.200', 'border.default'),
      bg: useColorModeValue('white', 'bg.card'),
      color: useColorModeValue('gray.800', 'text.default'),
      borderRadius: 'xl',
      boxShadow: 'sm',
    },
    btn: {
      borderRadius: 'md',
      bg: 'accent.500',
      border: '1px solid',
      borderColor: 'accent.500',
      color: 'white',
      fontWeight: 'semibold',
      _hover: {
        bg: 'accent.600',
        borderColor: 'accent.600',
        transform: 'translateY(-1px)',
        boxShadow: 'md',
      },
    },
    btnGhost: {
      borderRadius: 'md',
      bg: useColorModeValue('white', 'whiteAlpha.100'),
      border: '1px solid',
      borderColor: useColorModeValue('brand.300', 'border.default'),
      color: useColorModeValue('brand.700', 'text.default'),
      fontWeight: 'semibold',
      _hover: {
        bg: useColorModeValue('brand.50', 'whiteAlpha.200'),
      },
    },
  };

  const [emailItems, setEmailItems] = useState([]);
  const [waItems, setWaItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  // AlertDialog state
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const cancelRef = React.useRef();

  async function load() {
    setLoading(true);
    try {
      const [{ data: emails }, { data: whatsapps }] = await Promise.all([
        axiosInstance.get('/subscribers'),
        axiosInstance.get('/whatsapp-subscribers').catch(() => ({ data: { items: [] } }))
      ]);
      setEmailItems(emails?.items || []);
      setWaItems(whatsapps?.items || []);
    } catch {
      toast({ status: 'error', title: 'Error cargando suscriptores' });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  async function sendNewsletter() {
    setIsAlertOpen(false); // Close dialog
    setSending(true);
    try {
      const { data } = await axiosInstance.post('/subscribers/send-newsletter');
      toast({ 
        status: 'success', 
        title: 'Boletín Enviado', 
        description: `Se ha enviado a ${data.sent} suscriptores.`
      });
    } catch (e) {
      console.error("Error sending newsletter:", e);
      toast({ 
        status: 'error', 
        title: 'Error al enviar boletín',
        description: e.response?.data?.error || e.message || 'Error desconocido'
      });
    } finally {
      setSending(false);
    }
  }

  const activeCount = emailItems.filter(i => i.active).length;
  const activeWaCount = waItems.filter(i => i.active).length;

  return (
    <Box {...bw.box} p={4}>
      <Flex justify="space-between" align="center" mb={1} wrap="wrap" gap={4}>
        <Box>
          <Heading fontSize="lg" color={headingColor}>Boletín (Newsletter)</Heading>
          <Text fontSize="sm" color={subTextColor}>
            Gestiona los suscriptores y envía el último devocional por correo.
          </Text>
        </Box>
        <Flex gap={2} wrap="wrap">
          <Button
            {...bw.btnGhost}
            leftIcon={<FiRefreshCw />}
            onClick={load}
            isLoading={loading}
          >
            Refrescar
          </Button>
          <Button
            {...bw.btn}
            leftIcon={<FiMail />}
            onClick={() => setIsAlertOpen(true)}
            isLoading={sending}
            loadingText="Enviando..."
            isDisabled={activeCount === 0}
          >
            Enviar Boletín Semanal
          </Button>
        </Flex>
      </Flex>

      <Flex mb={4} mt={2} gap={2} wrap="wrap">
        <Badge colorScheme="green" p={2} borderRadius="md">
          {activeCount} Emails Activos
        </Badge>
        <Badge colorScheme="gray" p={2} borderRadius="md">
          {emailItems.length} Emails Totales
        </Badge>
        <Badge colorScheme="whatsapp" p={2} borderRadius="md">
          {activeWaCount} WA Activos
        </Badge>
        <Badge colorScheme="gray" p={2} borderRadius="md">
          {waItems.length} WA Totales
        </Badge>
      </Flex>

      {/* Tabla */}
      {loading ? (
        <Flex justify="center" align="center" py={8}>
          <Spinner thickness="3px" speed="0.65s" />
        </Flex>
      ) : (
        <Box overflowX="auto">
          <Table
            size="sm"
            variant="simple"
            sx={{ 'th, td, tr, thead, tbody': { borderColor: 'brand.200' } }}
            border="1px solid"
            borderColor="brand.200"
            mb={8}
          >
            <Thead bg={theadBg}>
              <Tr>
                <Th borderColor="brand.200">Email</Th>
                <Th borderColor="brand.200">Estado</Th>
                <Th borderColor="brand.200">Fecha Suscripción</Th>
              </Tr>
            </Thead>
            <Tbody>
              {emailItems.map((r) => (
                <Tr key={r.id} _hover={{ bg: rowHoverBg }}>
                  <Td borderColor="brand.200">{r.email}</Td>
                  <Td borderColor="brand.200">
                    {r.active ? (
                      <Badge colorScheme="green">Activo</Badge>
                    ) : (
                      <Badge colorScheme="red">Baja</Badge>
                    )}
                  </Td>
                  <Td borderColor="brand.200">
                    {new Date(r.created_at).toLocaleDateString()}
                  </Td>
                </Tr>
              ))}
              {emailItems.length === 0 && (
                <Tr>
                  <Td colSpan={3} textAlign="center" py={4} borderColor="brand.200">
                    No hay suscriptores por email.
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>

          <Heading fontSize="md" color={headingColor} mb={2}>Suscriptores WhatsApp</Heading>
          <Table
            size="sm"
            variant="simple"
            sx={{ 'th, td, tr, thead, tbody': { borderColor: 'brand.200' } }}
            border="1px solid"
            borderColor="brand.200"
          >
            <Thead bg={waTheadBg}>
              <Tr>
                <Th borderColor="brand.200">Número</Th>
                <Th borderColor="brand.200">Estado</Th>
                <Th borderColor="brand.200">Fecha Suscripción</Th>
              </Tr>
            </Thead>
            <Tbody>
              {waItems.map((r) => (
                <Tr key={r.id} _hover={{ bg: rowHoverBg }}>
                  <Td borderColor="brand.200">{r.phone_code} {r.phone_number}</Td>
                  <Td borderColor="brand.200">
                    {r.active ? (
                      <Badge colorScheme="green">Activo</Badge>
                    ) : (
                      <Badge colorScheme="red">Baja</Badge>
                    )}
                  </Td>
                  <Td borderColor="brand.200">
                    {new Date(r.created_at).toLocaleDateString()}
                  </Td>
                </Tr>
              ))}
              {waItems.length === 0 && (
                <Tr>
                  <Td colSpan={3} textAlign="center" py={4} borderColor="brand.200">
                    No hay suscriptores por WhatsApp aún.
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* AlertDialog de Confirmación */}
      <AlertDialog
        isOpen={isAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setIsAlertOpen(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg={useColorModeValue('white', 'gray.800')}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold" color={headingColor}>
              Enviar Resumen Semanal
            </AlertDialogHeader>

            <AlertDialogBody color={subTextColor}>
              ¿Estás seguro de enviar el Resumen Semanal? Esto recopilará los devocionales, estudios bíblicos y eventos recientes, y los enviará a todos los suscriptores activos en formato de resumen.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setIsAlertOpen(false)}>
                Cancelar
              </Button>
              <Button colorScheme="blue" onClick={sendNewsletter} ml={3} isLoading={sending}>
                Sí, Enviar Boletín
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <PushNotificationSection bw={bw} headingColor={headingColor} subTextColor={subTextColor} />
    </Box>
  );
}

// Podríamos exportarlo directamente, pero para mantenerlo limpio, 
// agregamos el formulario de Push debajo.

function PushNotificationSection({ bw, headingColor, subTextColor }) {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [url, setUrl] = useState('');
  const [sendingPush, setSendingPush] = useState(false);
  const toast = useToast();

  const handleSendPush = async () => {
    if (!title.trim() || !message.trim()) {
      toast({ title: 'Completa los campos', description: 'Título y Mensaje son obligatorios', status: 'warning' });
      return;
    }
    setSendingPush(true);
    try {
      const { data } = await axiosInstance.post('/push/send', { title, message, url });
      toast({ title: 'Notificaciones enviadas', description: `Se enviaron ${data.count} notificaciones.`, status: 'success' });
      setTitle('');
      setMessage('');
      setUrl('');
    } catch (e) {
      toast({ title: 'Error al enviar', description: e.response?.data?.error || e.message, status: 'error' });
    } finally {
      setSendingPush(false);
    }
  };

  return (
    <Box {...bw.box} p={4} mt={8}>
      <Heading fontSize="lg" color={headingColor} mb={1}>Enviar Notificación Push</Heading>
      <Text fontSize="sm" color={subTextColor} mb={4}>
        Envía una alerta directa a los teléfonos de los hermanos suscritos.
      </Text>

      <Flex direction="column" gap={3} maxW="500px">
        <Box>
          <Text fontSize="sm" fontWeight="bold" mb={1}>Título</Text>
          <input 
            type="text" 
            placeholder="Ej: Nuevo Devocional" 
            value={title} 
            onChange={(e) => setTitle(e.target.value)}
            style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
          />
        </Box>
        <Box>
          <Text fontSize="sm" fontWeight="bold" mb={1}>Mensaje</Text>
          <textarea 
            placeholder="Ej: Ya está disponible el devocional de esta semana." 
            value={message} 
            onChange={(e) => setMessage(e.target.value)}
            style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
          />
        </Box>
        <Box>
          <Text fontSize="sm" fontWeight="bold" mb={1}>Enlace (Opcional)</Text>
          <input 
            type="text" 
            placeholder="Ej: /devocionales" 
            value={url} 
            onChange={(e) => setUrl(e.target.value)}
            style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
          />
        </Box>
        <Button {...bw.btn} onClick={handleSendPush} isLoading={sendingPush} alignSelf="flex-start" mt={2}>
          Enviar Notificación a Todos
        </Button>
      </Flex>
    </Box>
  );
}
